
import { ConfigTabProps } from "./types";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { GuidedPromptEditor } from "./GuidedPromptEditor";

export const GeneralConfigTab = ({ selectedAgent, updateAgentConfig }: ConfigTabProps) => {
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);

  const generateAIPrompt = async () => {
    setIsGeneratingPrompt(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-agent-prompt', {
        body: {
          agentName: selectedAgent.name,
          description: selectedAgent.description,
          category: selectedAgent.category,
          capabilities: selectedAgent.capabilities,
          trainingLevel: selectedAgent.trainingLevel || 'basic',
          tone: selectedAgent.tone || 'professional'
        }
      });

      if (error) throw new Error(error.message);
      
      updateAgentConfig({ prompt: data.generatedPrompt });
      toast.success("AI prompt generated successfully");
    } catch (error) {
      console.error("Error generating prompt:", error);
      toast.error("Failed to generate AI prompt");
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  const handlePromptChange = (newPrompt: string) => {
    updateAgentConfig({ prompt: newPrompt });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="agent-name">Agent Name</Label>
        <Input 
          id="agent-name"
          value={selectedAgent.name}
          onChange={(e) => updateAgentConfig({ name: e.target.value })}
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="agent-description">Description</Label>
        <Textarea 
          id="agent-description"
          value={selectedAgent.description}
          onChange={(e) => updateAgentConfig({ description: e.target.value })}
          className="min-h-[100px]"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="agent-category">Category</Label>
        <Select 
          value={selectedAgent.category}
          onValueChange={(value) => updateAgentConfig({ category: value })}
        >
          <SelectTrigger id="agent-category">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="specialized">Specialized</SelectItem>
            <SelectItem value="general">General</SelectItem>
            <SelectItem value="assistant">Assistant</SelectItem>
            <SelectItem value="analyst">Analyst</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <GuidedPromptEditor
        value={selectedAgent.prompt || ""}
        onChange={handlePromptChange}
        agentType={selectedAgent.name}
        isReadOnly={selectedAgent.id === 'brigai-main'}
      />
      
      {selectedAgent.id !== 'brigai-main' && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={generateAIPrompt}
            disabled={isGeneratingPrompt}
            className="gap-2"
          >
            <Sparkles className="h-4 w-4" />
            {isGeneratingPrompt ? "Generating..." : "Generate New Prompt"}
          </Button>
        </div>
      )}
      
      <div className="space-y-2">
        <Label>Capabilities</Label>
        <div className="flex flex-wrap gap-2 pt-2">
          {selectedAgent.capabilities.map(capability => (
            <Badge key={capability}>
              {capability.split('-').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ')}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
};
